/**
 * Default row in the end-of-session miss list: German term on the left, the
 * translation you were after on the right, your own answer underneath.
 *
 * Used by Nouns and Vokabeln — both ask "what does this word mean?", so both
 * are reviewed the same way. Der/Die/Das and Einbürgerungstest ask something
 * else and ship their own rows.
 *
 * Your own wrong answer is on the row on purpose. "der Schlüssel — key" is a
 * re-read; "you wrote: lock" is a diagnosis, and the pair is what makes the
 * list worth scrolling.
 */
import type { MissRowProps } from '@/apps/types';
import { articleBorder, articleText, type Article } from '@/lib/gender';
import { cn } from '@/lib/utils';

/**
 * The only payload field this row reads. Nouns always carry an article;
 * Vokabeln carries one for nouns and `null` for verbs and adjectives, which
 * simply renders untinted.
 */
type ArticledPayload = { article: Article | null };

type Props = MissRowProps<ArticledPayload> & {
  /**
   * How the user produced the answer. Nouns are typed, Vokabeln options are
   * picked — saying "wrote" for a tap would be a small lie in a view whose
   * whole job is to report accurately what happened.
   */
  verb?: 'wrote' | 'chose';
  /** Which translation list to show. Matches the card back's primary language. */
  primary_lang?: string;
};

export function SharedMissRow({ card, user_answer, verb = 'wrote', primary_lang = 'en' }: Props) {
  const answer = (card.translations[primary_lang] ?? []).join(', ');
  const article = card.payload.article;

  return (
    <MissRowFrame article={article}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
        <span className="font-display text-base font-semibold">
          {article ? <span className={articleText(article)}>{article} </span> : null}
          {card.de}
        </span>
        <span className="text-base">
          {answer || <span className="text-muted-foreground text-sm italic">no translation</span>}
        </span>
      </div>
      <UserAnswer user_answer={user_answer} verb={verb} />
    </MissRowFrame>
  );
}

/**
 * Shared shell for every miss row, so the four apps' rows line up as one list
 * instead of four stacked designs.
 */
export function MissRowFrame({
  article = null,
  children,
}: {
  article?: Article | null;
  children: React.ReactNode;
}) {
  return (
    <li className={cn('flex flex-col gap-0.5 border-l-4 py-2.5 pl-3', articleBorder(article))}>
      {children}
    </li>
  );
}

/**
 * The second line. "I don't know" is reported as skipped rather than as a
 * wrong guess — the user told us they had nothing, and printing an empty
 * "you wrote:" would read as a bug.
 */
export function UserAnswer({
  user_answer,
  verb,
}: {
  user_answer: string | null;
  verb: 'wrote' | 'chose';
}) {
  if (user_answer === null) {
    return <span className="text-muted-foreground text-sm italic">skipped</span>;
  }
  return (
    <span className="text-muted-foreground text-sm">
      you {verb}: <span className="text-destructive">{user_answer}</span>
    </span>
  );
}
